// Cookie consent state + consent-aware script injection, with Google Consent Mode v2.
// Analytics category: PostHog + analytics_storage (GA4 inside GTM).
// Marketing category: ad_storage / ad_user_data / ad_personalization (ad tags inside GTM;
// Meta/TikTok tags must require ad_storage in the GTM UI). Essential is always granted.
// Basic Consent Mode: every Google consent type defaults to DENIED before anything loads,
// GTM loads only after the visitor grants analytics or marketing, and nothing is pushed
// to dataLayer before a choice exists (lib/track.js), so nothing can be replayed.
// IDs are per environment (REACT_APP_*). A missing ID means that script never loads.

export const CONSENT_KEY = "cookie_consent";
export const CONSENT_EVENT = "cookie-consent-changed";
export const OPEN_PREFS_EVENT = "open-cookie-preferences";

const POSTHOG_KEY = (process.env.REACT_APP_POSTHOG_KEY || "").trim();
const POSTHOG_HOST = (process.env.REACT_APP_POSTHOG_HOST || "").trim();
const GTM_ID = (process.env.REACT_APP_GTM_ID || "").trim();

function gtag() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments); // eslint-disable-line prefer-rest-params
}

function consentModeState(consent) {
  const a = consent && consent.analytics ? "granted" : "denied";
  const m = consent && consent.marketing ? "granted" : "denied";
  return { analytics_storage: a, ad_storage: m, ad_user_data: m, ad_personalization: m };
}

// Must run before GTM is ever injected; runs once, at first import.
function initConsentMode() {
  if (typeof window === "undefined" || window.__consent_mode_default) return;
  window.__consent_mode_default = true;
  gtag("consent", "default", {
    ...consentModeState(null),
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500,
  });
  gtag("set", "ads_data_redaction", true);
}
initConsentMode();

// True once the visitor has made a choice that allows Google tags to receive events.
export function dataLayerAllowed() {
  const c = getConsent();
  return !!(c && (c.analytics || c.marketing) && GTM_ID);
}

export function getConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveConsent({ analytics, marketing }) {
  const consent = {
    essential: true,
    analytics: !!analytics,
    marketing: !!marketing,
    timestamp: new Date().toISOString(),
  };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
  } catch {
    /* ignore */
  }
  applyConsent(consent);
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: consent }));
  return consent;
}

function injectPostHog() {
  if (!POSTHOG_KEY || !POSTHOG_HOST || window.__posthog_loaded) return;
  window.__posthog_loaded = true;
  const s = document.createElement("script");
  s.innerHTML = `!(function (t, e) { var o, n, p, r; e.__SV || ((window.posthog = e), (e._i = []), (e.init = function (i, s, a) { function g(t, e) { var o = e.split("."); 2 == o.length && ((t = t[o[0]]), (e = o[1])), (t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); }); } ((p = t.createElement("script")).type = "text/javascript"), (p.crossOrigin = "anonymous"), (p.async = !0), (p.src = s.api_host.replace(".i.posthog.com", "-assets.i.posthog.com") + "/static/array.js"), (r = t.getElementsByTagName("script")[0]).parentNode.insertBefore(p, r); var u = e; for (void 0 !== a ? (u = e[a] = []) : (a = "posthog"), u.people = u.people || [], u.toString = function (t) { var e = "posthog"; return "posthog" !== a && (e += "." + a), t || (e += " (stub)"), e; }, u.people.toString = function () { return u.toString(1) + ".people (stub)"; }, o = "init me ws ys ps bs capture je Di ks register register_once register_for_session unregister unregister_for_session Ps getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty Es $s createPersonProfile Is opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing Ss debug xs getPageViewId captureTraceFeedback captureTraceMetric".split(" "), n = 0; n < o.length; n++) g(u, o[n]); e._i.push([i, s, a]); }), (e.__SV = 1)); })(document, window.posthog || []); posthog.init("${POSTHOG_KEY}", { api_host: "${POSTHOG_HOST}", person_profiles: "identified_only", session_recording: { recordCrossOriginIframes: true, capturePerformance: false } });`;
  document.head.appendChild(s);
}

function injectGTM() {
  if (!GTM_ID || window.__gtm_loaded) return;
  window.__gtm_loaded = true;
  const s = document.createElement("script");
  s.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${GTM_ID}');`;
  document.head.appendChild(s);
}

// Loads static-key scripts (PostHog, GTM) for the granted categories. Additive:
// scripts already injected are never re-injected.
export function applyConsent(consent) {
  if (!consent) return;
  gtag("consent", "update", consentModeState(consent));
  if (consent.analytics) injectPostHog();
  else if (window.posthog && window.posthog.opt_out_capturing) window.posthog.opt_out_capturing();
  if (consent.analytics || consent.marketing) injectGTM();
}
